<script setup lang="ts">
  import { computed } from 'vue';
  import PosUiIcon from '@/components/pos-v2/ui/PosUiIcon.vue';
  import { Globals } from '@/modules/Globals';
  import { useRestaurantStore } from '@/stores/restaurant.store';

  const restaurantStore = useRestaurantStore();

  const logoUrl = computed(() =>
    restaurantStore.restaurant?.logo
      ? `${Globals.uploadsPath}/${restaurantStore.restaurant.logo}`
      : 'assets/img/restaurant-logo.png'
  );

  const restaurantName = computed(
    () => restaurantStore.restaurant?.name?.trim() || ''
  );

  const currencySymbol = computed(
    () => restaurantStore.currency?.symbol ?? '$'
  );
</script>

<template>
  <div class="whats-new-scene-code">
    <div class="member-card">
      <div class="card-top">
        <img class="card-logo" :src="logoUrl" alt="" />
        <span class="card-program">
          {{
            restaurantName
              ? $t('whatsNew.club.step3.cardProgram', { name: restaurantName })
              : $t('club')
          }}
        </span>
        <PosUiIcon class="card-watermark" name="wallet-membership" :size="28" />
      </div>
      <div class="card-identity">
        <span class="card-name">{{ $t('whatsNew.club.step3.cardName') }}</span>
        <span class="card-number">
          {{ $t('whatsNew.club.step3.cardMember') }}
        </span>
      </div>
      <div class="card-figures">
        <div class="figure-block">
          <span class="figure-value">
            {{ $t('whatsNew.club.step3.cardPoints') }}
          </span>
        </div>
        <div class="figure-block is-end">
          <span class="figure-value">
            {{ currencySymbol }}{{ $t('whatsNew.club.step3.cardBalance') }}
          </span>
          <span class="figure-caption">
            {{ $t('whatsNew.club.step3.cardBalanceCaption') }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
  .whats-new-scene-code {
    @apply tw-flex tw-items-center tw-justify-center tw-px-3;

    .member-card {
      @apply tw-relative tw-flex tw-w-56 tw-flex-col tw-gap-3 tw-overflow-hidden tw-rounded-2xl tw-bg-orange-600 tw-px-4 tw-py-3.5 tw-text-white tw-shadow-md;

      &::after {
        content: '';
        @apply tw-pointer-events-none tw-absolute tw-inset-0;
        background: linear-gradient(
          135deg,
          rgba(255, 255, 255, 0.14),
          transparent 55%
        );
      }

      .card-top {
        @apply tw-relative tw-flex tw-items-center tw-gap-2;

        .card-logo {
          @apply tw-h-7 tw-w-7 tw-flex-none tw-rounded-md tw-bg-white tw-object-contain;
        }

        .card-program {
          @apply tw-min-w-0 tw-flex-1 tw-truncate tw-text-sm tw-font-semibold;
        }

        .card-watermark {
          @apply tw-flex-none tw-text-white/20;
        }
      }

      .card-identity {
        @apply tw-relative tw-flex tw-flex-col;

        .card-name {
          @apply tw-truncate tw-text-lg tw-font-bold tw-leading-tight;
        }

        .card-number {
          @apply tw-text-[11px] tw-text-white/70;
        }
      }

      .card-figures {
        @apply tw-relative tw-flex tw-items-end tw-justify-between tw-gap-3;

        .figure-block {
          @apply tw-flex tw-flex-none tw-flex-col;

          .figure-value {
            @apply tw-text-xl tw-font-extrabold tw-tabular-nums tw-leading-tight;
          }

          .figure-caption {
            @apply tw-whitespace-pre-line tw-text-[10px] tw-leading-tight tw-text-white/80;
          }

          &.is-end {
            @apply tw-ml-auto tw-items-end tw-text-right;
          }
        }
      }
    }
  }

  @media (min-width: 768px) {
    .whats-new-scene-code {
      .member-card {
        @apply tw-w-60 tw-gap-4 tw-px-5 tw-py-4;

        .card-logo {
          @apply tw-h-8 tw-w-8 tw-rounded-lg;
        }

        .card-figures .figure-block .figure-value {
          @apply tw-text-2xl;
        }
      }
    }
  }

  @media (max-width: 767px) {
    .whats-new-scene-code {
      .member-card {
        @apply tw-w-52 tw-gap-2 tw-px-3 tw-py-2.5;

        .card-figures .figure-block {
          .figure-value {
            @apply tw-text-lg;
          }

          .figure-caption {
            @apply tw-hidden;
          }
        }
      }
    }
  }
</style>
